/* eslint-disable compat/compat */
import React, { useCallback, useEffect } from "react";
import { isPrintMedia } from "src/utils/is-print-media";

import { useDebouncedCallback } from "use-debounce";

export const useEnsureSameCardHeadersHeight = (
  ref: React.RefObject<HTMLDivElement>
) => {
  const isPrint = isPrintMedia();
  const headers = ref.current?.querySelectorAll("header");

  const doResize = useCallback(() => {
    const cards = ref.current?.querySelectorAll<HTMLElement>("article");
    const cardsHeaders =
      ref.current?.querySelectorAll<HTMLElement>("article header");

    const isGrid = Array.from(cards || []).every(
      (val, index, arr) => val.offsetTop === arr[0].offsetTop
    );

    if (!isGrid) {
      console.debug("Not a grid layout, skipping header height adjustment");
      resetAllResize("height", cardsHeaders);
      return;
    }

    forceResize("height", cardsHeaders);
  }, [ref, ref.current]);

  const handleResize = useDebouncedCallback(doResize, 200, { leading: true });

  const resizeObserver = React.useMemo(() => {
    try {
      const r = new ResizeObserver((entries) => {
        handleResize();
      });

      return r;
    } catch {
      //console.warn("ResizeObserver not supported");
      return undefined;
    }
  }, [handleResize]);

  useEffect(() => {
    if (!resizeObserver || isPrint)
      window.addEventListener("scroll", handleResize);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    handleResize();

    headers?.forEach((header) => {
      resizeObserver?.observe(header);
    });

    return () => {
      window.removeEventListener("scroll", handleResize);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      resizeObserver?.disconnect();
    };
  }, [handleResize, headers, isPrint, resizeObserver]);
};

const forceResize = (
  what: "height" | "width",
  items: NodeListOf<HTMLElement> | null | undefined
) => {
  if (!items) return;
  const clientWhat = what === "height" ? "clientHeight" : "clientWidth";
  items.forEach((item) => {
    item.style[what] = "auto"; // reset height to auto before measuring
  });
  const maxSize = Math.max(
    ...Array.from(items).map((header) => header[clientWhat])
  );
  items.forEach((header) => {
    header.style[what] = `${maxSize}px`;
  });
};

const resetAllResize = (
  what: "height" | "width",
  items: NodeListOf<HTMLElement> | null | undefined
) => {
  if (!items) return;
  items.forEach((item) => {
    item.style[what] = "auto";
  });
};
